import { useEffect, useState } from "react";
import { getGithubResource } from "./request";

interface DashboardStats {
  total_repos: number;
  total_contributors: number;
  total_commits: number;
  total_prs: number;
  new_repos_last_week: number;
  new_contributors_last_week: number;
  new_commits_last_week: number;
  new_prs_last_week: number;
  last_updated: string;
}

export interface TopContributorProps {
  top_contributors: {
    [username: string]: {
      avatarUrl: string;
      commits: number;
    };
  };
  last_updated: string;
}

export interface ContributorsCardProps {
  title: string;
  data: any;
  type: "List" | "Ring" | "ListCommits";
  isBelow800: boolean;
  info: any;
}

export interface recentCommits {
  recent_commits: Commit[];
  last_updated: string;
}

export interface Commit {
  author: {
    name: string;
    avatarUrl: string;
    user: {
      login: string;
    };
  };
  message: string;
  committedDate: string;
  oid: string;
  repository: {
    nameWithOwner: string;
  };
}

interface MonthlyCommitsData {
  monthly_commits: {
    [month: number]: number;
  };

  last_updated: string;
}

interface ActiveRepositoriesData {
  monthly_active_repos: {
    [month: number]: number;
  };

  last_updated: string;
}

interface ActiveContributorsData {
  active_contributors: string;
  last_updated: string;
}

export const useDashboardStats = (type: string = "cardano") => {
  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { response } = await getGithubResource({ endpoint: `statistics?topic=${type}` });
      if (response) {
        setData(response);
      }
      setLoading(false);
    })();
  }, [type]);

  return { data, loading };
};

export const useContributorStats = (type: string = "cardano") => {
  const [contributorsData, setContributorsData] =
    useState<TopContributorProps | null>(null);
  const [contributorsLoading, setContributorsLoading] = useState(true);
  const [contributorsError, setContributorsError] = useState(null);

  useEffect(() => {
    (async () => {
      const { response, error } = await getGithubResource({
        endpoint: `top_contributors?topic=${type}`,
      });
      if (error) {
        setContributorsError(error);
        setContributorsData(null);
      }
      if (response) {
        setContributorsData(response);
        setContributorsError(null);
      }
      setContributorsLoading(false);
    })();
  }, [type]);

  return { contributorsData, contributorsLoading, contributorsError };
};

export const useMonthlyCommits = (type: string = "cardano") => {
  const [chartData, setChartData] = useState<MonthlyCommitsData | null>(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMonthlyCommits = async () => {
      try {
        const { response, error } = await getGithubResource({
          endpoint: `monthly_commits?topic=${type}`,
        });

        if (error) {
          setError(error);
          setChartData(null);
        } else {
          setChartData(response);
          setError(null);
        }
      } catch (err) {
        setError("An error occurred while fetching the data");
      } finally {
        setChartLoading(false);
      }
    };

    fetchMonthlyCommits();
  }, [type]);

  return { chartData, chartLoading, error };
};

export const useActiveRepos = (type: string = "cardano") => {
  const [activeRepoData, setActiveRepoData] =
    useState<ActiveRepositoriesData | null>(null);
  const [activeRepoLoading, setActiveRepoLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActiveRepo = async () => {
      try {
        const { response, error } = await getGithubResource({
          endpoint: `active_repos?topic=${type}`,
        });

        if (error) {
          setError(error);
          setActiveRepoData(null);
        } else {
          setActiveRepoData(response);
          setError(null);
        }
      } catch (err) {
        setError("An error occurred while fetching the data");
      } finally {
        setActiveRepoLoading(false);
      }
    };

    fetchActiveRepo();
  }, [type]);

  return { activeRepoData, activeRepoLoading, error };
};

export const useActiveContributors = (type: string = "cardano") => {
  const [ActiveContributorsData, setActiveContributorsData] =
    useState<ActiveContributorsData | null>(null);
  const [ActiveContributorsLoading, setActiveContributorsLoading] =
    useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActiveContributors = async () => {
      try {
        const { response, error } = await getGithubResource({
          endpoint: `active_contributors?topic=${type}`,
        });

        if (error) {
          setError(error);
          setActiveContributorsData(null);
        } else {
          setActiveContributorsData(response);
          setError(null);
        }
      } catch (err) {
        setError("An error occurred while fetching the data");
      } finally {
        setActiveContributorsLoading(false);
      }
    };

    fetchActiveContributors();
  }, [type]);

  return { ActiveContributorsData, ActiveContributorsLoading, error };
};

export const useCommitHistory = (type: string = "cardano") => {
  const [commits, setCommits] = useState<recentCommits | null>(null);
  const [commitsLoading, setCommitsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCommits = async () => {
      try {
        const { response, error } = await getGithubResource({
          endpoint: `recent_commits?topic=${type}`,
        });

        if (error) {
          setError(error);
          setCommits(null);
        } else {
          setCommits(response);
          setError(null);
        }
      } catch (err) {
        setError("An error occurred while fetching commit history.");
      } finally {
        setCommitsLoading(false);
      }
    };

    fetchCommits();
  }, [type]);

  return { commits, commitsLoading, error };
};
